import { useState, useMemo, useCallback, useRef } from "react";
import type { BeforeCapture, DropResult } from "@hello-pangea/dnd";
import type { UseGrowerAssignmentProps, GrowerInfo, GrowerSortOption, JobCardInfo } from "./types";
import { getJobGrowerName, groupJobsByGrower, expandSelectionToJobIds } from "./assignment.helpers";
import { parseDraggableId, makeGrowerDraggableId, makeJobDraggableId } from "./draggableId.helpers";

const UNASSIGNED_DROPPABLE_ID = "unassigned";

export function useGrowerAssignment<TEntity extends { id: string }>(props: UseGrowerAssignmentProps<TEntity>) {
	const { jobs, entities, assignments, onAssign, explodedGrowers, onExplodeGrower, onRegroupGrower } = props;

	// Multi-select state (stores prefixed draggable IDs: "grower:X" or "job:Y")
	const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
	const lastClickedItem = useRef<string | null>(null);
	const [draggingSelectedCount, setDraggingSelectedCount] = useState(0);
	const [growerSortOption, setGrowerSortOption] = useState<GrowerSortOption>("name-asc");

	const clearSelection = useCallback(() => {
		setSelectedItems(new Set());
		lastClickedItem.current = null;
	}, []);

	// Group jobs by grower name to get grower info
	const growers = useMemo((): GrowerInfo[] => {
		const growerMap = new Map<string, number>();
		for (const job of jobs) {
			const name = getJobGrowerName(job);
			if (name) {
				growerMap.set(name, (growerMap.get(name) || 0) + 1);
			}
		}
		return Array.from(growerMap.entries())
			.map(([name, jobCount]) => ({ name, jobCount }))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [jobs]);

	// Jobs grouped by grower name (Map<growerName, JobCardInfo[]>)
	const jobsByGrower = useMemo(() => groupJobsByGrower(jobs), [jobs]);

	// Unassigned growers: NOT exploded AND no jobs assigned to any entity
	const unassignedGrowers = useMemo(() => {
		const filtered = growers.filter((g) => {
			// Exploded growers are shown as individual job cards, not grower cards
			if (explodedGrowers.has(g.name)) return false;
			// A grower is unassigned only if NONE of its jobs are assigned
			const growerJobs = jobsByGrower.get(g.name) || [];
			return growerJobs.every((j) => !assignments[j.jobId]);
		});
		return filtered.sort((a, b) => {
			switch (growerSortOption) {
				case "name-asc":
					return a.name.localeCompare(b.name);
				case "name-desc":
					return b.name.localeCompare(a.name);
				case "count-desc":
					return b.jobCount - a.jobCount || a.name.localeCompare(b.name);
				case "count-asc":
					return a.jobCount - b.jobCount || a.name.localeCompare(b.name);
			}
		});
	}, [growers, assignments, growerSortOption, explodedGrowers, jobsByGrower]);

	// Unassigned jobs for exploded growers
	const unassignedJobs = useMemo((): JobCardInfo[] => {
		const result: JobCardInfo[] = [];
		for (const growerName of explodedGrowers) {
			const growerJobs = jobsByGrower.get(growerName) || [];
			for (const job of growerJobs) {
				if (!assignments[job.jobId]) {
					result.push(job);
				}
			}
		}
		return result;
	}, [explodedGrowers, jobsByGrower, assignments]);

	// Growers grouped by entity (non-exploded growers only)
	const growersByEntity = useMemo(() => {
		const map = new Map<string, GrowerInfo[]>();
		for (const entity of entities) {
			map.set(entity.id, []);
		}
		for (const grower of growers) {
			// Skip exploded growers — they're shown as individual job cards
			if (explodedGrowers.has(grower.name)) continue;

			// Check if any of this grower's jobs are assigned to an entity
			const growerJobs = jobsByGrower.get(grower.name) || [];
			// Find the first assigned entity for this grower (they should all be the same
			// when not exploded, since grower cards assign all jobs together)
			const entityId = growerJobs.find((j) => assignments[j.jobId])
				? assignments[growerJobs.find((j) => assignments[j.jobId])!.jobId]
				: undefined;

			if (entityId && map.has(entityId)) {
				map.get(entityId)!.push(grower);
			}
		}
		return map;
	}, [growers, entities, assignments, explodedGrowers, jobsByGrower]);

	// Jobs grouped by entity (exploded growers only)
	const jobsByEntity = useMemo(() => {
		const map = new Map<string, JobCardInfo[]>();
		for (const entity of entities) {
			map.set(entity.id, []);
		}
		for (const growerName of explodedGrowers) {
			const growerJobs = jobsByGrower.get(growerName) || [];
			for (const job of growerJobs) {
				const entityId = assignments[job.jobId];
				if (entityId && map.has(entityId)) {
					map.get(entityId)!.push(job);
				}
			}
		}
		return map;
	}, [entities, explodedGrowers, jobsByGrower, assignments]);

	// Flat ordered list of all visible draggable IDs (for shift-click range selection)
	const visibleItemOrder = useMemo(() => {
		const order: string[] = [];

		// Unassigned panel: collapsed grower cards first
		for (const g of unassignedGrowers) {
			order.push(makeGrowerDraggableId(g.name));
		}

		// Unassigned panel: exploded grower sections with individual job cards
		const unassignedJobsByGrower = new Map<string, JobCardInfo[]>();
		for (const job of unassignedJobs) {
			const existing = unassignedJobsByGrower.get(job.growerName);
			if (existing) existing.push(job);
			else unassignedJobsByGrower.set(job.growerName, [job]);
		}
		for (const growerName of Array.from(unassignedJobsByGrower.keys()).sort()) {
			for (const job of unassignedJobsByGrower.get(growerName)!) {
				order.push(makeJobDraggableId(job.jobId));
			}
		}

		// Assigned items per entity
		for (const entity of entities) {
			for (const g of growersByEntity.get(entity.id) || []) {
				order.push(makeGrowerDraggableId(g.name));
			}
			for (const j of jobsByEntity.get(entity.id) || []) {
				order.push(makeJobDraggableId(j.jobId));
			}
		}

		return order;
	}, [unassignedGrowers, unassignedJobs, entities, growersByEntity, jobsByEntity]);

	// Handle click with optional shift for range selection (uses prefixed draggable IDs)
	const handleItemClick = useCallback(
		(draggableId: string, shiftKey: boolean) => {
			if (shiftKey && lastClickedItem.current) {
				// Range selection: select all items between lastClicked and current
				const lastIdx = visibleItemOrder.indexOf(lastClickedItem.current);
				const curIdx = visibleItemOrder.indexOf(draggableId);
				if (lastIdx !== -1 && curIdx !== -1) {
					const start = Math.min(lastIdx, curIdx);
					const end = Math.max(lastIdx, curIdx);
					setSelectedItems((prev) => {
						const next = new Set(prev);
						for (let i = start; i <= end; i++) {
							next.add(visibleItemOrder[i]);
						}
						return next;
					});
				} else {
					// Fallback: just add the single item
					setSelectedItems((prev) => {
						const next = new Set(prev);
						next.add(draggableId);
						return next;
					});
				}
				lastClickedItem.current = draggableId;
				return;
			}
			// Normal toggle
			setSelectedItems((prev) => {
				const next = new Set(prev);
				if (next.has(draggableId)) {
					next.delete(draggableId);
				} else {
					next.add(draggableId);
				}
				return next;
			});
			lastClickedItem.current = draggableId;
		},
		[visibleItemOrder],
	);

	// Backward-compatible handleGrowerClick that delegates to handleItemClick
	const handleGrowerClick = useCallback(
		(name: string, shiftKey: boolean) => {
			handleItemClick(makeGrowerDraggableId(name), shiftKey);
		},
		[handleItemClick],
	);

	// Handle drag start — track multi-drag count
	const handleBeforeCapture = useCallback(
		(before: BeforeCapture) => {
			if (selectedItems.has(before.draggableId)) {
				setDraggingSelectedCount(selectedItems.size);
			} else {
				setDraggingSelectedCount(0);
			}
		},
		[selectedItems],
	);

	// Handle drag end
	const handleDragEnd = useCallback(
		(result: DropResult) => {
			const { draggableId, destination } = result;
			setDraggingSelectedCount(0);
			if (!destination) return;

			const targetId = destination.droppableId;
			const parsed = parseDraggableId(draggableId);
			const isMultiDrag = selectedItems.has(draggableId) && selectedItems.size > 1;

			const newAssignments = { ...assignments };

			if (isMultiDrag) {
				// Multi-drag: expand all selected items to job IDs
				const jobIds = expandSelectionToJobIds(selectedItems, jobsByGrower);
				for (const jobId of jobIds) {
					if (targetId === UNASSIGNED_DROPPABLE_ID) {
						delete newAssignments[jobId];
					} else {
						newAssignments[jobId] = targetId;
					}
				}
			} else if (parsed.type === "grower") {
				// Grower card drag — assign/unassign ALL jobs for this grower
				const growerJobs = jobsByGrower.get(parsed.key) || [];
				for (const job of growerJobs) {
					if (targetId === UNASSIGNED_DROPPABLE_ID) {
						delete newAssignments[job.jobId];
					} else {
						newAssignments[job.jobId] = targetId;
					}
				}
			} else {
				// Job card drag — assign/unassign single job
				const jobId = parsed.key;
				if (targetId === UNASSIGNED_DROPPABLE_ID) {
					delete newAssignments[jobId];
					// Auto-explode: when dragging a job to unassigned, explode the grower
					// so the user can see individual job cards
					const job = jobs.find((j) => j.id === jobId);
					if (job) {
						const growerName = getJobGrowerName(job);
						if (growerName && !explodedGrowers.has(growerName)) {
							onExplodeGrower(growerName);
						}
					}
				} else {
					newAssignments[jobId] = targetId;
				}
			}

			onAssign(newAssignments);
			setSelectedItems(new Set());
			lastClickedItem.current = null;
		},
		[assignments, onAssign, selectedItems, jobsByGrower, jobs, explodedGrowers, onExplodeGrower],
	);

	// Bulk assignment handlers — operate on job IDs
	const assignAllToEntity = useCallback(
		(entityId: string) => {
			const newAssignments: Record<string, string> = {};
			for (const job of jobs) {
				newAssignments[job.id] = entityId;
			}
			onAssign(newAssignments);
		},
		[jobs, onAssign],
	);

	const assignUnassignedToEntity = useCallback(
		(entityId: string) => {
			const newAssignments = { ...assignments };
			for (const job of jobs) {
				if (!assignments[job.id]) {
					newAssignments[job.id] = entityId;
				}
			}
			onAssign(newAssignments);
		},
		[assignments, jobs, onAssign],
	);

	const unassignAllFromEntity = useCallback(
		(entityId: string) => {
			const newAssignments = { ...assignments };
			for (const key of Object.keys(newAssignments)) {
				if (newAssignments[key] === entityId) {
					delete newAssignments[key];
				}
			}
			onAssign(newAssignments);
		},
		[assignments, onAssign],
	);

	const assignSelectedToEntity = useCallback(
		(entityId: string) => {
			if (selectedItems.size === 0) return;
			const newAssignments = { ...assignments };
			const jobIds = expandSelectionToJobIds(selectedItems, jobsByGrower);
			for (const jobId of jobIds) {
				newAssignments[jobId] = entityId;
			}
			onAssign(newAssignments);
			setSelectedItems(new Set());
		},
		[assignments, selectedItems, jobsByGrower, onAssign],
	);

	const unassignAll = useCallback(() => {
		onAssign({});
	}, [onAssign]);

	return {
		growers,
		unassignedGrowers,
		unassignedJobs,
		growersByEntity,
		jobsByEntity,
		jobsByGrower,
		explodedGrowers,
		onExplodeGrower,
		onRegroupGrower,
		selectedItems,
		selectedGrowers: selectedItems, // backward compat alias
		handleItemClick,
		handleGrowerClick, // backward compat alias
		clearSelection,
		draggingSelectedCount,
		handleBeforeCapture,
		handleDragEnd,
		assignAllToEntity,
		assignUnassignedToEntity,
		unassignAllFromEntity,
		assignSelectedToEntity,
		unassignAll,
		growerSortOption,
		setGrowerSortOption,
		UNASSIGNED_DROPPABLE_ID,
	};
}
